import { createBrowserHistory } from 'history';
import { Reducer, applyMiddleware, compose, createStore } from 'redux';
import { createReduxHistoryContext } from 'redux-first-history';
import createSagaMiddleware from 'redux-saga';
import { ForkEffect, all } from 'redux-saga/effects';

import pendoMiddleware from 'modules/pendo';
import segmentMiddleware from 'modules/segment';

/**
 * Composes single-argument functions from right to left. The rightmost function can take multiple
 * arguments as it provides the signature for the resulting composite function.
 */
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

/**
 * History object used by router middleware for Redux. Exported for use by the core/table, which
 * for some reason makes use of it.
 */
export const routerMiddlewareHistory = createBrowserHistory();

/**
 * Middleware for dispatching router navigation events via Redux.
 */
const { routerMiddleware, routerReducer, createReduxHistory } = createReduxHistoryContext({
  history: routerMiddlewareHistory,
  reduxTravelling: true,
});

/**
 * Route reducer. Exported for use by the redux store.
 */
export const reduxRouterReducer = routerReducer;

/**
 * History object used by router middleware for Redux. Exported for use by the router reducer.
 */
export const createHistory = createReduxHistory;

/**
 * Middleware and global state for root saga.
 */
const sagaMiddleware = createSagaMiddleware();
const sagasList: ForkEffect[] = [];
const sagasDirectory: Record<string, boolean> = {};

/**
 * Helper to get the args of the saga.
 */
export const getSagaArgs = (saga: ForkEffect) => {
  return saga.payload?.args || [];
};

/**
 * Helper to add a single saga to sagasList. Checks if it already exists in sagasDirectory to
 * determine if saga should be added.
 */
export const addSingleSaga = (saga: ForkEffect) => {
  const args = getSagaArgs(saga);
  const action = args[0];

  if (!sagasDirectory[action]) {
    sagasDirectory[action] = true;
    sagasList.push(saga);
    return true;
  }
  return false;
};

/**
 * Adds an array of sagas to the root saga store.
 *
 * Note: Since sagas aren't imported directly by any files naturally, they're frequently missed by
 * webpack compilation and new sagas will fail to run. The solution is to make sure that the saga
 * file is directly imported by the `index.js` file that sits within the containing folder structure.
 * One important caveat is that any dependencies the saga file has will need to be imported directly
 * from the source files themselves and not through the `index.js` file layer as this causes a
 * circular reference in Webpack.
 */
export const addToRootSaga = (sagaArray: ForkEffect[]) => {
  sagaArray.forEach((saga) => {
    addSingleSaga(saga);
  });
  return true;
};

/**
 * Root saga that runs all attached effects in the app.
 */
function* rootSaga() {
  try {
    yield all(sagasList);
  } catch (e) {
    yield e;
  }
}

/**
 * Instantiates a new Redux store, initializes it with reducers/middleware, and returns it.
 */
export const initializeStore = (rootReducer: Reducer) => {
  const store = createStore(
    rootReducer,
    composeEnhancers(
      applyMiddleware(routerMiddleware, sagaMiddleware, segmentMiddleware, pendoMiddleware),
    ),
  );
  sagaMiddleware.run(rootSaga);
  return store;
};
